/**
 * Admin Ticket Events Page - shows events for ticket operations
 * Used by ticket operators who don't have full admin access
 */

import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { PageLoader } from "@/components/PageLoader";
import {
  IconTicket,
  IconCalendar,
  IconAlertTriangle,
  IconQrCode,
  IconBanknotes,
} from "@/components/Icons";
import { getTelegram } from "@/lib/telegram";
import { useAuth } from "@/lib/auth";
import { useAdminTicketEvents } from "./hooks";

type FilterType = "all" | "active" | "past";

function formatDate(date: string | null): string {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function AdminTicketEventsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { hasAnyPermission } = useAuth();
  const [filter, setFilter] = useState<FilterType>("active");

  const { events, isLoading, error } = useAdminTicketEvents();

  // Permission checks
  const canScan = hasAnyPermission(["tickets.scan", "admin.full"]);
  const canIssue = hasAnyPermission(["tickets.issue", "admin.full"]);

  // Telegram back button
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => navigate("/admin");
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Filter events
  const now = new Date();
  const filteredEvents = events?.filter((event) => {
    if (filter === "all") return true;
    const endDate = event.endDate ? new Date(event.endDate) : null;
    const startDate = event.startDate ? new Date(event.startDate) : null;
    
    if (filter === "active") {
      if (endDate) return endDate >= now;
      if (startDate) return startDate >= now;
      return true;
    }
    
    if (filter === "past") {
      if (endDate) return endDate < now;
      return false;
    }
    
    return true;
  }) ?? [];

  if (isLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка мероприятий..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <IconAlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-slate-600">Ошибка загрузки</p>
            <p className="text-sm text-slate-500 mt-1">{error.message}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <IconTicket className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Билеты</h1>
                <p className="text-sm text-slate-500">Выберите мероприятие</p>
              </div>
            </div>
          </div>

          {/* Permission badges */}
          <div className="flex gap-2 mb-4">
            {canScan && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                <IconQrCode className="w-3.5 h-3.5" />
                Сканирование
              </div>
            )}
            {canIssue && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
                <IconBanknotes className="w-3.5 h-3.5" />
                Выдача за наличные
              </div>
            )}
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === "all"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Все
            </button>
            <button
              onClick={() => setFilter("active")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === "active"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Активные
            </button>
            <button
              onClick={() => setFilter("past")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === "past"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              Прошедшие
            </button>
          </div>
        </div>

        {/* Events list */}
        <div className="p-4 space-y-3">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <IconCalendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {filter === "all"
                  ? "Нет мероприятий с билетами"
                  : filter === "active"
                  ? "Нет активных мероприятий"
                  : "Нет прошедших мероприятий"}
              </p>
            </div>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                {/* Event info */}
                <div className="p-4 border-b border-slate-100">
                  <div className="flex gap-4">
                    {/* Cover image */}
                    {event.coverImage ? (
                      <img
                        src={event.coverImage}
                        alt=""
                        className="w-16 h-16 rounded-xl object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                        <IconCalendar className="w-6 h-6 text-slate-400" />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">
                        {event.title}
                      </h3>
                      
                      {/* Date */}
                      <p className="text-sm text-slate-500 mt-0.5">
                        {formatDate(event.startDate)}
                      </p>

                      {/* Stats */}
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <IconTicket className="w-3.5 h-3.5" />
                          <span>{event.ticketsCount} билетов</span>
                        </div>
                        {event.usedTicketsCount > 0 && (
                          <div className="flex items-center gap-1 text-xs text-green-600">
                            <IconQrCode className="w-3.5 h-3.5" />
                            <span>{event.usedTicketsCount} отмечено</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex divide-x divide-slate-100">
                  {canScan && (
                    <Link
                      to={`/admin/tickets/validate?eventId=${event.id}`}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-green-600 hover:bg-green-50 active:bg-green-100 transition-colors"
                    >
                      <IconQrCode className="w-4 h-4" />
                      Сканировать
                    </Link>
                  )}
                  {canIssue && (
                    <Link
                      to={`/admin/tickets/cash?eventId=${event.id}`}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-amber-600 hover:bg-amber-50 active:bg-amber-100 transition-colors"
                    >
                      <IconBanknotes className="w-4 h-4" />
                      За наличные
                    </Link>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AdminTicketEventsPage;
